import type { ReactNode } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  FICHA_FIELDS,
  getByPath,
  resolveOptions,
  type FichaCata,
  type FieldSpec,
  type SeleccionDescriptores,
} from '@escuela/ficha-domain'
import { Segmented } from './controls/segmented'
import { ScoreSlider } from './controls/score-slider'
import { PillSelect } from './controls/pill-select'
import { DescriptorTree } from './controls/descriptor-tree'
import { CepaCombobox } from './controls/cepa-combobox'
import { applyDependentResets, otrosPathFor, setByPath } from './paths'

interface FieldRowProps {
  field: FieldSpec
  ficha: FichaCata
  onChange: (ficha: FichaCata) => void
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
}

/** Renderiza una fila de campo con el primitivo web segun `field.control`. */
export function FieldRow({ field, ficha, onChange }: FieldRowProps): ReactNode {
  const value = getByPath(ficha, field.path)
  const set = (v: unknown): void => onChange(setByPath(ficha, field.path, v))

  const otrosPath = otrosPathFor(field.path)
  const otrosValue = otrosPath ? asString(getByPath(ficha, otrosPath)) : ''
  const setOtros = (v: string): void => {
    if (otrosPath) onChange(setByPath(ficha, otrosPath, v))
  }

  return (
    <div data-field-path={field.path} className="flex flex-col gap-2">
      <Label>{field.labelKey}</Label>
      {renderControl()}
    </div>
  )

  function renderControl(): ReactNode {
    switch (field.control) {
      case 'text':
        return (
          <Input
            value={asString(value)}
            onChange={(e) => set(e.target.value)}
          />
        )
      case 'numeric-text':
        return (
          <Input
            inputMode="numeric"
            value={asString(value)}
            onChange={(e) => set(e.target.value)}
          />
        )
      case 'textarea':
        return (
          <Textarea
            rows={3}
            value={asString(value)}
            onChange={(e) => set(e.target.value)}
          />
        )
      case 'segmented':
        return (
          <Segmented
            options={resolveOptions(field, ficha)}
            value={asString(value)}
            ariaLabel={field.labelKey}
            onChange={(v) =>
              onChange(applyDependentResets(setByPath(ficha, field.path, v), field.path))
            }
          />
        )
      case 'score-slider':
        return (
          <ScoreSlider
            value={typeof value === 'number' ? value : -1}
            ariaLabel={field.labelKey}
            leftLabel={field.sliderLabels?.min}
            rightLabel={field.sliderLabels?.max}
            onChange={(v) => set(v)}
          />
        )
      case 'multi-pill':
        return (
          <PillSelect
            options={resolveOptions(field, ficha)}
            value={asStringArray(value)}
            onChange={(v) => set(v)}
            otros={otrosValue}
            onOtrosChange={setOtros}
            ariaLabel={field.labelKey}
          />
        )
      case 'descriptor': {
        // El slider "Condición de la fruta" (nariz/gusto.condicionFruta) va
        // anidado dentro del cuadro Frutal, no como fila aparte (paridad app).
        const cfPath = `${field.section}.condicionFruta`
        const cfField = FICHA_FIELDS.find((f) => f.path === cfPath)
        const cfValue = getByPath(ficha, cfPath)
        return (
          <DescriptorTree
            value={(value as SeleccionDescriptores | undefined) ?? {}}
            wineColor={ficha.apariencia.colorPrincipal}
            onChange={(v) => set(v)}
            fruitCondition={
              cfField
                ? {
                    value: typeof cfValue === 'number' ? cfValue : -1,
                    onChange: (v) => onChange(setByPath(ficha, cfPath, v)),
                    labels: cfField.sliderLabels,
                    ariaLabel: cfField.labelKey,
                    fieldPath: cfPath,
                  }
                : undefined
            }
          />
        )
      }
      case 'cepa':
        return (
          <CepaCombobox
            value={asStringArray(value)}
            onChange={(v) => set(v)}
            otros={otrosValue}
            onOtrosChange={setOtros}
          />
        )
      default:
        return null
    }
  }
}
